// stores/profile.ts
import { defineStore } from 'pinia'
import { computed } from 'vue'
import type { RemovableRef } from '@vueuse/core'
import { useLocalStorage } from '@vueuse/core'
import { createStoreBase } from '@/presentation/stores/resources/storeBase.ts'
import { UserGender } from '@/core/domain/resources/enums/UserGender.ts'
import DependencyContainer from '@/presentation/configuration/DependencyContainer.ts'
import { FetchMeUseCase } from '@/core/useCases/profile/FetchMeUseCase.ts'
import { storeEventBus, useEventListener } from '@/presentation/stores/utils/storeEventBus.ts'
import { isUserSelfView, type UserSelfView } from '@/core/domain/users/LocalUser.ts'
import { useAuthentication } from '@/presentation/composables/auth/useAuthentication.ts'
import { useAuthStore } from '@/presentation/stores/auth.ts'
import * as Sentry from '@sentry/vue'

/**
 * Interface pour les préférences utilisateur
 */
interface IPreferences {
  menuOpened: boolean
  darkMode: boolean
  lastUsedSchoolId?: number
  lastUsedSchoolPeriodId?: number
}

/**
 * Store pour la gestion du profil utilisateur et des préférences utilisateur
 */
export const useProfileStore = defineStore('profile', () => {
  // SERVICES & DEPENDENCIES
  const { fullName, picture, firstnameInitial } = useAuthentication()

  // STORE BASE
  const storeBase = createStoreBase(['fetchingProfile'])

  // STATE
  const meRef: RemovableRef<UserSelfView | null> = useLocalStorage('me', null)
  const preferences: RemovableRef<IPreferences> = useLocalStorage('preferences', {
    menuOpened: true,
    darkMode: false
  })

  /**
   * Abonnement sur le bus pour écouter l'évènement d'authentification
   */
  useEventListener('auth-initialized', async ({ authenticated }) => {
    console.log('Auth initialized', authenticated)
    if (!authenticated) return
    try { await getMe() } catch(error) { console.error('Erreur lors de la récupération du profil après authentification:', error) }
  })

  /**
   * On configure Sentry dès chargement du profil
   */
  useEventListener('user-profile-loaded', async ({ user }) => {
    if (user) {
      Sentry.setUser({ id: String(user.identifier) })

      // Enrichir le contexte Sentry avec les rôles et métadonnées
      // Permet de filtrer et grouper les erreurs par type d'utilisateur
      const authStore = useAuthStore()
      const userRoles = authStore.getRoles

      Sentry.setTags({
        // Liste des rôles (séparés par virgule pour recherche full-text dans Sentry)
        userRoles: userRoles.length > 0 ? userRoles.join(',') : 'none',
        // Segment utilisateur (backoffice vs school)
        userSegment: authStore.hasBackOfficeRole ? 'backoffice' : authStore.hasSchoolRole ? 'school' : 'unknown',
        // Indicateur multi-rôles
        hasMultipleRoles: userRoles.length > 1
      })
    }
  })

  /**
   * Abonnement sur le bus pour écouter l'évènement de déconnexion
   */
  useEventListener('auth-logged-out', () => {
    $reset()
  })

  // ACTIONS
  /**
   * Récupère le profil de l'utilisateur connecté
   */
  async function getMe(): Promise<void> {
    try {
      storeBase.loading.value['fetchingProfile'] = true
      storeBase.resetError()

      const user = await DependencyContainer.resolve(FetchMeUseCase).execute()

      if (user && isUserSelfView(user)) {
        meRef.value = user
        storeBase.updateLastUpdated()

        // Émettre un événement indiquant que le profil a été chargé
        storeEventBus.emit('user-profile-loaded', {
          user
        })
      }
    } catch (error) {
      storeBase.setError(error)
    } finally {
      storeBase.loading.value['fetchingProfile'] = false
    }
  }

  /**
   * Bascule l'état d'ouverture du menu
   */
  function toggleMenuOpen() {
    preferences.value.menuOpened = !preferences.value.menuOpened
  }

  /**
   * Change manuellement l'état d'ouverture du menu
   * @param open
   */
  function setMenuOpen(open: boolean) {
    preferences.value.menuOpened = open
    console.log(preferences.value.menuOpened)
  }

  /**
   * Bascule le mode sombre
   */
  function toggleDarkMode() {
    preferences.value.darkMode = !preferences.value.darkMode
  }

  /**
   * Enregistre le dernier établissement et période utilisés
   */
  function setLastUsedSchool(schoolId: number, periodId?: number) {
    preferences.value.lastUsedSchoolId = schoolId
    if (periodId) {
      preferences.value.lastUsedSchoolPeriodId = periodId
    }
  }

  /**
   * Réinitialise l'état du store
   */
  function $reset() {
    storeBase.resetBase()
    meRef.value = null
    // Nous conservons les préférences
  }

  // GETTERS (COMPUTED)

  /**
   * Encapsule meRef dans un computed pour éviter sa modification
   */
  const me = computed(() => meRef.value)

  /**
   * Récupère l'initiale du prénom de l'utilisateur
   */
  const getFirstNameInitial = computed(() => firstnameInitial.value ?? 'U')

  /**
   * Récupère le nom complet de l'utilisateur
   */
  const getFullName = computed(() => fullName.value ?? 'Utilisateur')

  /**
   * Récupère l'URL de l'avatar de l'utilisateur
   */
  const getAvatar = computed(() =>  picture.value)

  const getGender = computed(() => { return meRef.value?.gender ?? UserGender.Values.UNSPECIFIED })

  /**
   * Vérifie si le menu est ouvert
   */
  const isMenuOpened = computed(() => preferences.value.menuOpened)

  /**
   * Vérifie si le mode sombre est activé
   */
  const isDarkMode = computed(() => preferences.value.darkMode)

  /**
   * Récupère le dernier établissement utilisé
   */
  const getLastUsedSchoolId = computed(() => preferences.value.lastUsedSchoolId)

  /**
   * Récupère la dernière période utilisée
   */
  const getLastUsedSchoolPeriodId = computed(() => preferences.value.lastUsedSchoolPeriodId)

  /**
   * Récupère le genre formaté de l'utilisateur
   */
  const getFormattedGender = computed(() => {
    if (!meRef.value?.gender) return undefined
    return UserGender.label(meRef.value.gender)
  })

  /**
   * Vérifie si le profil est chargé
   */
  const isProfileLoaded = computed(() => {
    return !!meRef.value
  })


  return {
    // State
    ...storeBase,

    // Actions
    getMe,
    toggleMenuOpen,
    toggleDarkMode,
    setMenuOpen,
    setLastUsedSchool,
    $reset,

    // Getters
    me,
    getFirstNameInitial,
    getFullName,
    getAvatar,
    getGender,
    isMenuOpened,
    isDarkMode,
    getLastUsedSchoolId,
    getLastUsedSchoolPeriodId,
    getFormattedGender,
    isProfileLoaded
  }
})

export type ProfileStore = ReturnType<typeof useProfileStore>
